// Client/src/pages/digi/Invoice/TemplateDesigner/useDesignState.js
//
// The designer's document state: the draft design, undo/redo (100 steps), and
// autosave. A change is saved 1.5 s after the last edit; the server's draftRev
// guards against two tabs overwriting each other (409 → "conflict", autosave
// stops until reload). `flush()` saves now and resolves when the draft on the
// server matches what is on screen (used before Submit and Preview PDF).

import { useCallback, useEffect, useRef, useState } from "react";
import { getTemplate, saveTemplateDraft } from "../Components/templateApi";

const HISTORY_LIMIT = 100;
const SAVE_DELAY_MS = 1500;
const COALESCE_MS = 1000;

export default function useDesignState(templateId) {
  const [template, setTemplate] = useState(null);
  const [draft, setDraft] = useState(null);
  const [issues, setIssues] = useState([]);
  const [status, setStatus] = useState("saved"); // saved | dirty | saving | error | conflict
  const [loadError, setLoadError] = useState("");
  const [historySize, setHistorySize] = useState({ past: 0, future: 0 });

  const draftRef = useRef(null);
  const revRef = useRef(0);
  const past = useRef([]);
  const future = useRef([]);
  const lastCoalesce = useRef({ key: null, at: 0 });
  const timer = useRef(null);
  const saving = useRef(null); // the in-flight save promise
  const dirty = useRef(false);
  const conflict = useRef(false);

  const syncHistory = () => setHistorySize({ past: past.current.length, future: future.current.length });

  const load = useCallback(async () => {
    setLoadError("");
    try {
      const t = await getTemplate(templateId);
      setTemplate(t);
      setIssues(t.issues || []);
      draftRef.current = t.draft;
      revRef.current = t.draftRev;
      setDraft(t.draft);
      past.current = [];
      future.current = [];
      dirty.current = false;
      conflict.current = false;
      setStatus("saved");
      syncHistory();
    } catch (e) {
      setLoadError(e.message || "Could not load the template");
    }
  }, [templateId]);

  useEffect(() => {
    load();
    return () => clearTimeout(timer.current);
  }, [load]);

  const save = useCallback(async () => {
    clearTimeout(timer.current);
    if (conflict.current) return;
    if (saving.current) {
      // A save is running; the newer edits go out right after it.
      await saving.current;
      if (dirty.current) return save();
      return;
    }
    if (!dirty.current) return;
    const sent = draftRef.current;
    dirty.current = false;
    setStatus("saving");
    saving.current = (async () => {
      try {
        const res = await saveTemplateDraft(templateId, sent, revRef.current);
        revRef.current = res.rev;
        setIssues(res.issues || []);
        setStatus(dirty.current ? "dirty" : "saved");
      } catch (e) {
        if (e.status === 409) {
          conflict.current = true;
          setStatus("conflict");
        } else {
          dirty.current = true;
          setStatus("error");
        }
      } finally {
        saving.current = null;
      }
    })();
    await saving.current;
    if (dirty.current && !conflict.current) {
      timer.current = setTimeout(save, SAVE_DELAY_MS);
    }
  }, [templateId]);

  const schedule = useCallback(() => {
    dirty.current = true;
    if (!conflict.current) setStatus("dirty");
    clearTimeout(timer.current);
    timer.current = setTimeout(save, SAVE_DELAY_MS);
  }, [save]);

  /**
   * Apply an edit. `next` is a design or a function of the current design.
   * `opts.coalesce` merges rapid edits with the same key (typing, nudging) into
   * one undo step.
   */
  const change = useCallback(
    (next, opts = {}) => {
      const current = draftRef.current;
      const value = typeof next === "function" ? next(current) : next;
      if (!value || value === current) return;
      const now = Date.now();
      const merge =
        opts.coalesce && lastCoalesce.current.key === opts.coalesce && now - lastCoalesce.current.at < COALESCE_MS;
      if (!merge) {
        past.current.push(current);
        if (past.current.length > HISTORY_LIMIT) past.current.shift();
      }
      lastCoalesce.current = { key: opts.coalesce || null, at: now };
      future.current = [];
      draftRef.current = value;
      setDraft(value);
      syncHistory();
      schedule();
    },
    [schedule]
  );

  const undo = useCallback(() => {
    if (!past.current.length) return;
    future.current.push(draftRef.current);
    draftRef.current = past.current.pop();
    lastCoalesce.current = { key: null, at: 0 };
    setDraft(draftRef.current);
    syncHistory();
    schedule();
  }, [schedule]);

  const redo = useCallback(() => {
    if (!future.current.length) return;
    past.current.push(draftRef.current);
    draftRef.current = future.current.pop();
    lastCoalesce.current = { key: null, at: 0 };
    setDraft(draftRef.current);
    syncHistory();
    schedule();
  }, [schedule]);

  // Save immediately; resolves true when the server has what is on screen.
  const flush = useCallback(async () => {
    if (conflict.current) return false;
    if (dirty.current || saving.current) await save();
    return !dirty.current && !conflict.current;
  }, [save]);

  // Leaving the page with unsaved edits: best-effort save + browser prompt.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!dirty.current && !saving.current) return undefined;
      save();
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [save]);

  const patchTemplate = useCallback((patch) => setTemplate((t) => (t ? { ...t, ...patch } : t)), []);

  return {
    template,
    patchTemplate,
    draft,
    issues,
    status,
    loadError,
    reload: load,
    change,
    undo,
    redo,
    canUndo: historySize.past > 0,
    canRedo: historySize.future > 0,
    flush,
    hasUnsaved: status === "dirty" || status === "saving" || status === "error",
  };
}
